import {
  Box,
  Flex,
  Image,
  Input,
  InputGroup,
  InputLeftElement,
  Modal,
  ModalBody,
  ModalContent,
  ModalOverlay,
  Text,
  VStack,
} from "@chakra-ui/react";
import { SearchIcon } from "@chakra-ui/icons";
import { useMemo, useState } from "react";
import { normalizeTokenImageUrl } from "../../helpers/tokenImage";
import { ThemeColor } from "../../theme/palette";
import { formatSmartFixed6 } from "../../utils/format";
import { DRIP_BOX_BG, DRIP_TRACK_BG } from "./dripStyles";
import { DripTokenMeta } from "./dripStyles";

// Fallback decimals when a mint hasn't resolved on-chain yet (kySOL is 9).
const FALLBACK_DECIMALS = 9;

export interface DripRewardAssetOption {
  mint: string;
  symbol: string;
  image?: string;
  totalRaw: string;
  decimals: number;
}

interface DripRewardAssetSelectModalProps {
  isOpen: boolean;
  onClose: () => void;
  options: DripRewardAssetOption[];
  selectedMint: string;
  onSelect: (mint: string) => void;
}

const formatTotal = (raw: string, decimals: number) =>
  formatSmartFixed6(Number(raw) / Math.pow(10, decimals));

export const DripRewardAssetSelectModal = ({
  isOpen,
  onClose,
  options,
  selectedMint,
  onSelect,
}: DripRewardAssetSelectModalProps) => {
  const [query, setQuery] = useState("");

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options;
    return options.filter(
      (opt) =>
        opt.symbol.toLowerCase().includes(q) ||
        (q.length > 2 && opt.mint.toLowerCase().includes(q))
    );
  }, [options, query]);

  const handleSelect = (mint: string) => {
    onSelect(mint);
    setQuery("");
    onClose();
  };

  const handleClose = () => {
    setQuery("");
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} isCentered scrollBehavior="inside">
      <ModalOverlay bg="blackAlpha.700" />
      <ModalContent
        bg={DRIP_BOX_BG}
        border="1px solid"
        borderColor="whiteAlpha.200"
        borderRadius="2xl"
        w="100%"
        maxW="480px"
        mx={4}
        maxH="85vh"
      >
        <ModalBody p={4}>
          <Text fontSize="sm" fontWeight="semibold" color="white" mb={3}>
            Select reward asset
          </Text>
          <InputGroup mb={3} size="md">
            <InputLeftElement pointerEvents="none">
              <SearchIcon color="whiteAlpha.600" />
            </InputLeftElement>
            <Input
              placeholder="Search asset"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              bg={DRIP_TRACK_BG}
              border="1px solid"
              borderColor="whiteAlpha.200"
              color="white"
              _placeholder={{ color: "whiteAlpha.500" }}
              _focus={{ borderColor: ThemeColor.KAIROS, boxShadow: "none" }}
            />
          </InputGroup>

          <Box maxH="360px" overflowY="auto" mx={-1} px={1}>
            {filtered.length === 0 ? (
              <Text py={8} textAlign="center" color="whiteAlpha.600" fontSize="sm">
                No assets found
              </Text>
            ) : (
              <VStack spacing={1} align="stretch">
                {filtered.map((opt) => {
                  const isSelected = opt.mint === selectedMint;
                  return (
                    <Flex
                      key={opt.mint}
                      as="button"
                      type="button"
                      w="100%"
                      align="center"
                      justify="space-between"
                      gap={3}
                      px={3}
                      py={3}
                      borderRadius="xl"
                      bg={isSelected ? "rgba(95, 244, 244, 0.1)" : "transparent"}
                      border="1px solid"
                      borderColor={isSelected ? "rgba(95, 244, 244, 0.35)" : "transparent"}
                      transition="background 0.15s ease, border-color 0.15s ease"
                      _hover={{
                        bg: isSelected ? "rgba(95, 244, 244, 0.12)" : "whiteAlpha.100",
                      }}
                      onClick={() => handleSelect(opt.mint)}
                    >
                      <Flex align="center" gap={3} minW={0}>
                        <Image
                          src={normalizeTokenImageUrl(opt.image ?? "/unknown.svg")}
                          boxSize="32px"
                          borderRadius="full"
                          flexShrink={0}
                          fallbackSrc="/unknown.svg"
                        />
                        <Text fontWeight="semibold" color="white" fontSize="sm" noOfLines={1}>
                          {opt.symbol}
                        </Text>
                      </Flex>
                      <Box textAlign="right" flexShrink={0}>
                        <Text fontSize="sm" fontWeight="medium" color={ThemeColor.KAIROS}>
                          {formatTotal(opt.totalRaw, opt.decimals)}
                        </Text>
                        <Text fontSize="xs" color="whiteAlpha.600">
                          total
                        </Text>
                      </Box>
                    </Flex>
                  );
                })}
              </VStack>
            )}
          </Box>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export const buildDripRewardAssetOptions = (
  mints: string[],
  totalsByMint: Record<string, string>,
  tokenMetaByMint: Record<string, DripTokenMeta>,
  decimalsByMint: Record<string, number>
): DripRewardAssetOption[] =>
  mints.map((mint) => ({
    mint,
    symbol: tokenMetaByMint[mint]?.symbol ?? `${mint.slice(0, 4)}...${mint.slice(-4)}`,
    image: tokenMetaByMint[mint]?.image,
    totalRaw: totalsByMint[mint] ?? "0",
    decimals: decimalsByMint[mint] ?? FALLBACK_DECIMALS,
  }));
